import React from 'react';
import {Map, Set} from 'immutable';
import UserStore from 'stores/UserStore';
import {browserHistory} from 'react-router';
import connectToStores from 'alt-utils/lib/connectToStores';
import moment from 'moment';
import ManagerDashboardStore from 'stores/ManagerDashboardStore';
import ManagerDashboardActions from 'actions/ManagerDashboardActions';
import './ManagerCTA.styl';
import DashboardCTA from 'components/dashboard/DashboardCTA';
import {SearchIcon} from '@components/icon/SearchIcon';
import {TeamIcon} from '@components/icon/TeamIcon';
import {ChartLineUpIcon} from '@components/icon/ChartLineUpIcon';
import {ListCheckIcon} from '@components/icon/ListCheckIcon';
import UpcomingReviewsStore from 'stores/coaching/UpcomingReviewsStore';
import DirectReportsActions from 'actions/coaching/DirectReportsActions';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import DashboardDateRangeActions from 'actions/dashboard/DasbhoardDateRangeActions';
import {dashV2Path, getManagerIdIfNotAuthUser, shouldUpdate} from 'components/StatsPage2.utils';
import {CTARole} from 'components/dashboard/ManagerCTA.utils';

const widgetId = 'manager_cta';

// This list all the CTAs, in priority order as listed in the tickets EV-13524 and EV-13928.
// The path is the link to the next page when a CTA is clicked on, it is a lambda
// so that templating can be applied at runtime with the given params.
// The inboxTag is the tag that some CTA needs in order to form the link to the proper tab in the Inbox page,
// it must match the name of the actual tag.
// The color of each CTA icon is in this class' corresponding styl file.
const ctaTypes = [
  {
    roles: Set([CTARole.ADMIN, CTARole.MANAGER, CTARole.COACH]),
    type: 'UNREAD_SMART_ALERTS',
    inboxTag: 'Smart Alerts',
    path: params => '/inbox/items',
    query: params => ({
      unviewedOnly: false,
      pinnedOnly: false,
      folderId: params.inboxId,
      tagId: params.tagId,
    }),
  },
  {
    roles: Set([CTARole.ADMIN, CTARole.MANAGER, CTARole.COACH]),
    type: 'UNREAD_FEEDBACK_REQUESTS',
    inboxTag: 'Feedback Requested',
    path: params => '/inbox/items',
    query: params => ({
      unviewedOnly: false,
      pinnedOnly: false,
      folderId: params.inboxId,
      tagId: params.tagId,
    }),
  },
  {
    roles: Set([CTARole.ADMIN, CTARole.MANAGER, CTARole.COACH]),
    type: 'UNREAD_SHARES',
    inboxTag: 'Shared With Me',
    path: params => '/inbox/items',
    query: params => ({
      unviewedOnly: false,
      pinnedOnly: false,
      folderId: params.inboxId,
      tagId: params.tagId,
    }),
  },
  {
    roles: Set([CTARole.ADMIN, CTARole.MANAGER]),
    type: 'REPS_WITH_NO_CALLS',
    path: params => `/${dashV2Path}${params.adminPath}${params.managerPath}/todays-conversations`,
    state: {
      showTooltip: true,
    },
  },
  {
    roles: Set([CTARole.ADMIN, CTARole.MANAGER]),
    type: 'REPS_WITH_NO_SCORES',
    daysBack: 60,
    path: params => `/${dashV2Path}${params.adminPath}${params.managerPath}/rep-skills`,
  },
  {
    roles: Set([CTARole.ADMIN, CTARole.MANAGER, CTARole.COACH]),
    type: 'REPS_WITH_NO_COACHING_PLANS',
    path: params =>
      `/${dashV2Path}${params.adminPath}${params.managerPath}${params.coachPath}/coaching-plans`,
  },
  {
    roles: Set([CTARole.ADMIN, CTARole.MANAGER, CTARole.COACH]),
    type: 'REVIEWS_SCHEDULED',
    path: params =>
      `/${dashV2Path}${params.adminPath}${params.managerPath}${params.coachPath}/coaching-plans`,
  },
  {
    roles: Set([CTARole.ADMIN]),
    type: 'MANAGERS_NOT_COACHING',
    path: params => `/${dashV2Path}${params.adminPath}/coaching-activity`,
  },
  {
    roles: Set([CTARole.ADMIN]),
    type: 'MANAGERS_WITH_NO_COACHING_PLANS',
    daysBack: 30,
    path: params => `/${dashV2Path}${params.adminPath}/coaching-plans`,
  },
];

/**
 * These are used for new accounts to fill out the normal CTA spots
 */
const newAccountCtas = [
  {
    type: 'NEW_ACCOUNT_SEARCH',
    label: 'Learn how to search for calls',
    icon: SearchIcon,
    link: 'https://support.mediafly.com/hc/en-us/articles/13640942385051',
  },
  {
    type: 'NEW_ACCOUNT_PLANS',
    label: 'Learn about Coaching Plans',
    icon: ChartLineUpIcon,
    link: 'https://support.mediafly.com/hc/en-us/sections/14407447947419-Coaching',
  },
];

const adminFakeCtas = [
  ...newAccountCtas,
  {
    type: 'NEW_ACCOUNT_TEAMS',
    label: 'Administer Your Teams',
    icon: TeamIcon,
    link: 'https://support.mediafly.com/hc/en-us/articles/13427895071643#h_01GTVE8MK14R5T42YP9QMNN8XV',
  },
];

const managerFakeCtas = [
  ...newAccountCtas,
  {
    type: 'NEW_ACCOUNT_SCORE',
    label: 'Learn how to score calls',
    icon: ListCheckIcon,
    link: 'https://support.mediafly.com/hc/en-us/articles/7682441716507-Build-Scorecard',
  },
];

/**
 * XXX Not specific to managers, rename
 */
class ManagerCTA extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onClick = this.onClick.bind(this);
  }

  static getStores() {
    return [ManagerDashboardStore, UpcomingReviewsStore, UserStore];
  }

  static getPropsFromStores({managerId}) {
    return {
      widgets: ManagerDashboardStore.getState().widgets,
      manager: UserStore.getState().users.get(managerId),
      teamIds: DirectReportsStore.getState().teams.map(t => t.id),
    };
  }

  async componentDidMount() {
    this.getWidget();
  }

  componentDidUpdate(prevProps) {
    const {role} = this.props;
    const useTeam = role === 'manager';
    // we only show the CTAs on ones own dash (including an admin viewing her own manager dash),
    // so there is no need to watch for other changes, like if managerId or coachId has changed
    if (useTeam && shouldUpdate(prevProps, this.props, true, false)) {
      this.getWidget();
    }
  }

  getWidget() {
    const {selectedTeam, role, managerId} = this.props;
    const useTeam = role === 'manager';
    if (useTeam && !selectedTeam) {
      return;
    }
    const teamId = !useTeam ? null : selectedTeam.id;
    // this widget does not care about dates yet
    const widgetConfig = {
      teamId: teamId,
      widgetId: widgetId,
      widgetType: 'CALL_TO_ACTION',
    };

    ManagerDashboardActions.getWidget.defer([widgetConfig, getManagerIdIfNotAuthUser(this.props)]);
    if (managerId && role !== 'coach') {
      DirectReportsActions.directReports.defer(managerId);
    }
  }

  onClick({path, inboxTag, daysBack, query: queryFunc = null, state = {}}) {
    const {adminId, managerId, coachId, inboxId} = this.props;
    // When an admin is also a manager, and he clicks at his own CTA, then we take him to the corresponding page
    // of his own manager dashboard
    const coachPath = coachId ? `/coach/${coachId}` : '';
    const managerPath = coachId
      ? managerId
        ? `/manager/${managerId}`
        : ''
      : `/manager/${managerId ? managerId : adminId}`;
    const adminPath = adminId ? `/admin/${adminId}` : '';
    if (
      inboxTag &&
      (!inboxId || !this.props.tagIdsByName || !this.props.tagIdsByName.get(inboxTag))
    ) {
      console.error(
        'inboxTag is specified but are missing info to form the link',
        inboxId,
        inboxTag,
        this.props.tagIdsByName
      );
      return;
    }
    const tagId = inboxTag && this.props.tagIdsByName ? this.props.tagIdsByName.get(inboxTag) : '';
    const params = {inboxId, tagId, coachPath, managerPath, adminPath};
    const pathname = path(params);
    const query = queryFunc ? queryFunc(params) : {};
    if (daysBack) {
      // both are inclusive, so together will be daysBack number of days
      const endDate = moment().subtract(1, 'day');
      const startDate = endDate.clone().subtract(daysBack - 1, 'days');
      DashboardDateRangeActions.setDateRange({startDate, endDate});
      state.blinkDates = true;
    }
    browserHistory.push({
      pathname,
      query,
      state,
    });
  }

  getTopN(dataArray, N) {
    const dataAsObject = {};
    dataArray.forEach(datum => {
      dataAsObject[datum.type] = datum;
    });
    const typeToData = Map(dataAsObject);
    // since ctaTypes is already in priority order, there is no need to sort
    const filtered = ctaTypes
      .map(candidate => {
        const datum = typeToData.get(candidate.type);
        return Object.assign(
          datum ? {count: datum.count, label: datum.label} : {count: 0},
          candidate
        );
      })
      .filter(cta => cta.count)
      .filter(cta => cta.roles.has(this.props.role));
    // XXX test code, take what I want to see
    // const len = filtered.length;
    // return len > N ? filtered.slice(len - N, len) : filtered;
    return filtered.slice(0, N);
  }

  render() {
    const {role, isNewAccount, widgets = Map()} = this.props;
    const isAdmin = role === 'admin';
    const smallClass = role === 'coach' ? 'cta-small' : '';
    const adminCtaClass = isAdmin ? 'admin-cta' : '';

    if (isNewAccount) {
      const fakeCtas = isAdmin ? adminFakeCtas : managerFakeCtas;
      return (
        <div className={`manager-cta ${adminCtaClass}`}>
          {fakeCtas.map((cta, i) => (
            <DashboardCTA key={i} cta={cta} />
          ))}
        </div>
      );
    }

    const widget = widgets.get(widgetId);
    if (!widget) return null;

    const data = widget.actions;
    if (!data) return null;

    const N = 3;
    const topN = this.getTopN(data, N);
    const len = topN.length;
    return (
      <div className={`manager-cta ${adminCtaClass} ${smallClass}`}>
        {len > 0 &&
          topN.map(cta => (
            <DashboardCTA key={cta.type} cta={cta} smallClass={smallClass} onClick={this.onClick} />
          ))}
        {len > 0 &&
          len < N &&
          [...Array(N - len).keys()].map(i => (
            <div key={i} className={`cta-filler ${smallClass}`} />
          ))}
      </div>
    );
  }
}

export default connectToStores(ManagerCTA);
